import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, CheckCircle2, XCircle, PhoneMissed } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface UserWithStatus {
  id: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  status: 'ringing' | 'rejected' | 'accepted';
  rejectedAt?: string;
}

interface VideoRingProgressModalProps {
  ringRequestId: string;
  initialLevel: number;
  initialTimeout: number;
  initialUsers: any[];
  onClose: () => void;
}

export function VideoRingProgressModal({
  ringRequestId,
  initialLevel,
  initialTimeout,
  initialUsers,
  onClose,
}: VideoRingProgressModalProps) {
  const { t } = useTranslation();

  console.log('[VideoRingProgressModal] 🎬 Component mounted with:', {
    ringRequestId,
    initialLevel,
    initialTimeout,
    usersCount: initialUsers.length,
    users: initialUsers.map(u => ({ id: u.id, name: u.display_name || `${u.first_name} ${u.last_name}` })),
  });

  const levelNameKeys = ['firstResponders', 'management', 'finalEscalation'];
  const getLevelName = (level: number) => {
    const key = levelNameKeys[level - 1];
    return key ? t(`videoRing.levelNames.${key}`) : `Level ${level}`;
  };

  const [currentLevel, setCurrentLevel] = useState(initialLevel);
  const [levelName, setLevelName] = useState(getLevelName(initialLevel));
  const [users, setUsers] = useState<UserWithStatus[]>(
    initialUsers.map(u => ({ ...u, status: 'ringing' as const }))
  );
  const [previousLevelUsers, setPreviousLevelUsers] = useState<UserWithStatus[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(initialTimeout);
  const [status, setStatus] = useState<'ringing' | 'accepted' | 'timeout' | 'cancelled'>('ringing');
  const [meetingUrl, setMeetingUrl] = useState<string | null>(null);
  const [acceptedBy, setAcceptedBy] = useState<{
    name: string;
    initials: string;
  } | null>(null);
  const isEscalatingRef = useRef(false);

  useEffect(() => {
    // Subscribe to ring request changes
    const channel = supabase
      .channel(`ring-progress-${ringRequestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'video_call_ring_requests' as any,
          filter: `id=eq.${ringRequestId}`,
        },
        async (payload: any) => {
          const ringRequest = payload.new;
          const metadata = ringRequest.metadata || {};
          
          console.log('[VideoRingProgressModal] Realtime update:', { 
            status: ringRequest.status, 
            level: ringRequest.current_level,
            rejections: metadata.rejections 
          });

          // Update rejections in real-time
          const rejections = metadata.rejections || [];
          setUsers(prev => prev.map(user => {
            const rejection = rejections.find((r: any) => r.user_id === user.id);
            if (rejection) {
              return { ...user, status: 'rejected' as const, rejectedAt: rejection.rejected_at };
            }
            return user;
          }));

          // Also update previous level users
          setPreviousLevelUsers(prev => prev.map(user => {
            const rejection = rejections.find((r: any) => r.user_id === user.id);
            if (rejection) {
              return { ...user, status: 'rejected' as const, rejectedAt: rejection.rejected_at };
            }
            return user;
          }));

        if (ringRequest.status === 'accepted') {
          setStatus('accepted');
          setMeetingUrl(ringRequest.meeting_url);
          
          // Mark accepted user
          setUsers(prev => prev.map(user => 
            user.id === ringRequest.accepted_by 
              ? { ...user, status: 'accepted' as const }
              : user
          ));
          
          // Buscar dados do consultor que aceitou
          if (ringRequest.accepted_by) {
            const { data: consultant } = await supabase
              .from('app_users')
              .select('first_name, last_name, display_name')
              .eq('id', ringRequest.accepted_by)
              .single();
            
            if (consultant) {
              const name = consultant.display_name || 
                          `${consultant.first_name} ${consultant.last_name}`;
              const initials = name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
              
              setAcceptedBy({ name, initials });
              toast.success(t('videoRing.toasts.callAcceptedBy', { name }));
            } else {
              toast.success(t('videoRing.toasts.callAccepted'));
            }
          } else {
            toast.success(t('videoRing.toasts.callAccepted'));
          }
          
          // Abrir link automaticamente se disponível
          if (ringRequest.meeting_url) {
            window.open(ringRequest.meeting_url, '_blank');
          }
        } else if (ringRequest.status === 'timeout') {
          setStatus('timeout');
          toast.error(t('videoRing.toasts.noConsultantsAvailable'));
          setTimeout(() => {
            onClose();
          }, 3000);
          } else if (ringRequest.status === 'cancelled') {
            setStatus('cancelled');
            onClose();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ringRequestId, onClose, t]);

  // Timer countdown - only decrements, does NOT call handleEscalate
  useEffect(() => {
    if (status !== 'ringing') return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, currentLevel]);
  // Reset escalation guard only when level actually changes
  useEffect(() => {
    isEscalatingRef.current = false;
  }, [currentLevel]);

  // Trigger escalation exactly once when timer hits 0
  useEffect(() => {
    if (timeRemaining === 0 && status === 'ringing' && !isEscalatingRef.current) {
      isEscalatingRef.current = true;
      handleEscalate();
    }
  }, [timeRemaining, status]);

  const handleEscalate = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('escalate-video-ring', {
        body: { ring_request_id: ringRequestId },
      });

      if (error) throw error;

      // B2) If status is timeout, set status and schedule close
      if (data.status === 'timeout') {
        setStatus('timeout');
        console.log('[VideoRingProgressModal] Escalation returned timeout, scheduling close');
        setTimeout(() => {
          onClose();
        }, 3000);
        return;
      }

      // B1) Fallback for unknown status
      if (!['ringing', 'accepted', 'timeout', 'cancelled'].includes(data.status || 'ringing')) {
        console.log('[VideoRingProgressModal] Unknown status from escalation:', data.status, '- closing');
        onClose();
        return;
      }

      // Save current level users to previous (mark non-rejected as missed)
      setPreviousLevelUsers(prev => [
        ...prev,
        ...users.map(u => u.status === 'ringing' ? { ...u, status: 'rejected' as const } : u)
      ]);

      setCurrentLevel(data.current_level);
      setTimeRemaining(data.timeout_seconds);
      setUsers(data.available_users.map((u: any) => ({ ...u, status: 'ringing' as const })));
      setLevelName(getLevelName(data.current_level));
    } catch (error) {
      console.error('Escalation failed:', error);
      toast.error(t('videoRing.toasts.failedToEscalate'));
    }
  };

  const handleCancel = async () => {
    try {
      // First get the ring request to get the correct call_id
      const { data: ringRequestData } = await supabase
        .from('video_call_ring_requests' as any)
        .select('call_id')
        .eq('id', ringRequestId)
        .single();

      const callIdToUpdate = (ringRequestData as any)?.call_id;

      // Update ring request status
      await supabase
        .from('video_call_ring_requests' as any)
        .update({ status: 'cancelled', ended_at: new Date().toISOString() })
        .eq('id', ringRequestId);

      // Update calls table with the CORRECT call_id (not ringRequestId)
      if (callIdToUpdate) {
        await supabase
          .from('calls')
          .update({ status: 'cancelled', ended_at: new Date().toISOString() })
          .eq('id', callIdToUpdate);
      }

      onClose();
    } catch (error) {
      console.error('Failed to cancel:', error);
    }
  };

  const progressPercent = ((initialTimeout - timeRemaining) / initialTimeout) * 100;

  const getUserName = (user: UserWithStatus) => 
    user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';

  const getUserInitials = (user: UserWithStatus) =>
    getUserName(user).split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  const renderUserStatus = (user: UserWithStatus) => {
    switch (user.status) {
      case 'accepted':
        return (
          <Badge variant="default" className="gap-1 bg-green-100 text-green-700 border-0">
            <CheckCircle2 className="h-3 w-3" />
            {t('videoRing.accepted', 'Atendeu')}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="gap-1 border-orange-300 text-orange-600">
            <XCircle className="h-3 w-3" />
            {t('videoRing.rejectedLabel', 'Recusou')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('videoRing.ringing')}
          </Badge>
        );
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogTitle className="text-center">
          {status === 'ringing' && t('videoRing.callingConsultants')}
          {status === 'accepted' && t('videoRing.callAccepted')}
          {status === 'timeout' && t('videoRing.noConsultantsAvailable')}
          {status === 'cancelled' && t('videoRing.callCancelled')}
        </DialogTitle>

        <div className="space-y-6 py-4">
          {status === 'ringing' && (
            <>
              {/* Level Indicator */}
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <Badge variant="outline">Nível {currentLevel}</Badge>
                  <h3 className="text-lg font-semibold">{levelName}</h3>
                </div>
                <Progress value={progressPercent} className="h-2" />
                <p className="text-sm text-muted-foreground">
                  {t('videoRing.callingCount', { count: users.filter(u => u.status === 'ringing').length })}
                </p>
                <div className="text-3xl font-bold text-primary tabular-nums">
                  {timeRemaining}s
                </div>
              </div>

              {/* Previous Level Users (if escalated) */}
              {previousLevelUsers.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Níveis Anteriores:</p>
                  <div className="space-y-1 opacity-60">
                    {previousLevelUsers.map((user) => (
                      <div key={user.id} className="flex items-center gap-2 p-1.5 rounded">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">{getUserInitials(user)}</AvatarFallback>
                        </Avatar>
                        <span className="flex-1 text-sm">{getUserName(user)}</span>
                        <Badge variant="outline" className="gap-1 text-xs border-orange-300 text-orange-600">
                          <PhoneMissed className="h-3 w-3" />
                          {user.status === 'rejected' ? 'Recusou' : 'Missed'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Users List */}
              <ScrollArea className="h-48 border rounded-lg p-2">
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">{getUserInitials(user)}</AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-sm">{getUserName(user)}</span>
                      {renderUserStatus(user)}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Cancel Button */}
              <Button onClick={handleCancel} variant="outline" className="w-full">
                <X className="mr-2 h-4 w-4" />
                {t('videoRing.cancelCall')}
              </Button>
            </>
          )}

          {status === 'accepted' && (
            <div className="text-center space-y-4">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
              
              {acceptedBy ? (
                <div className="space-y-3">
                  <div className="flex flex-col items-center gap-2">
                    <Avatar className="h-16 w-16 border-2 border-green-600">
                      <AvatarFallback className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xl font-bold">
                        {acceptedBy.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-lg">{acceptedBy.name}</p>
                      <p className="text-sm text-muted-foreground">{t('videoRing.acceptedYourCall')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">{t('videoRing.consultantAccepted')}</p>
              )}
              
              {meetingUrl && (
                <>
                  <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">{t('videoRing.meetingLink')}</p>
                    <a 
                      href={meetingUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                    >
                      {meetingUrl}
                    </a>
                  </div>
                  <Button 
                    onClick={() => window.open(meetingUrl, '_blank')}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {t('videoRing.openMeeting')}
                  </Button>
                </>
              )}
              
              <Button onClick={onClose} variant="outline" className="w-full">
                {t('common.close')}
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div className="text-center space-y-4">
              <XCircle className="h-16 w-16 text-destructive mx-auto" />
              <p className="text-muted-foreground">
                {t('videoRing.noConsultantsMessage')}
              </p>
              <Button onClick={onClose} className="w-full">
                {t('common.close')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
